'use client'

import { MoveRightIcon, XIcon } from 'lucide-react'
import Link from 'next/link'
import React, { useRef } from 'react'

import {
  aboutUsLinkGroup,
  brandLinkGroup,
  helpSupportLinkGroup,
  loggedInAccountLinkGroup,
  loggedOutAccountLinkGroup,
  partnersLinkGroup,
} from './siteLinks'
import { Button } from './ui/Button'
import { cn } from './ui/cn'
import { SheetClose, SheetContent, SheetTitle } from './ui/Sheet'
import { Wrapper } from './ui/Wrapper'

interface NavSheetProps {
  logoUrl?: string
  brandName?: string
  isLoggedIn?: boolean
  onLogIn?: () => void
  onLogOut?: () => void
  onSignUp?: () => void
  /** Closes the controlling <Sheet> (from SiteNav). */
  onRequestClose?: () => void
}

const Branding = Wrapper
const LinkGroup = Wrapper

export const NavSheet = ({
  logoUrl = '/brands/shipplay_dark.svg',
  brandName = 'Ship&Play',
  isLoggedIn = false,
  onLogIn,
  onLogOut,
  onSignUp,
  onRequestClose,
}: NavSheetProps): React.ReactElement => {
  const siteLinkGroups = [
    brandLinkGroup,
    helpSupportLinkGroup,
    aboutUsLinkGroup,
    partnersLinkGroup,
    isLoggedIn ? loggedInAccountLinkGroup : loggedOutAccountLinkGroup,
  ]

  // Set while we close this sheet in order to open a modal (Login / Create
  // Account). Read in onCloseAutoFocus to stop Radix from restoring focus to
  // the hamburger trigger, which would steal focus from the modal that just
  // opened. Normal closes leave this false so keyboard users land back on the
  // trigger.
  const handingOffToModalRef = useRef(false)

  const handleAccountLinkAction = (action?: 'logIn' | 'logOut' | 'signUp'): void => {
    // Login / Create Account open a Headless UI modal on top of this Radix
    // sheet. Two focus traps can't both be active, so close the sheet first —
    // its trap releases and the modal (opened below) captures focus. The
    // account links call preventDefault() to cancel their `#` navigation, which
    // makes the wrapping <SheetClose> skip its own close (Radix composes
    // handlers with checkForDefaultPrevented), so we must close explicitly.
    if (action === 'logIn' || action === 'signUp') {
      handingOffToModalRef.current = true
      onRequestClose?.()
    }

    if (action === 'logIn') onLogIn?.()
    if (action === 'logOut') onLogOut?.()
    if (action === 'signUp') onSignUp?.()
  }

  return (
    <SheetContent
      id="mobile-nav"
      side="right"
      showCloseButton={false}
      onCloseAutoFocus={(event) => {
        // When closing to hand off to a modal, don't let Radix pull focus back
        // to the hamburger trigger — that would break the modal's focus trap.
        if (handingOffToModalRef.current) {
          event.preventDefault()
          handingOffToModalRef.current = false
        }
      }}
      className={cn(
        'snplay:z-[120] snplay:size-full snplay:max-w-none',
        'snplay:gap-0 snplay:overflow-y-auto snplay:border-0',
        'snplay:bg-background snplay:p-0 snplay:text-ui-primary snplay:sm:max-w-none',
      )}
    >
      <SheetTitle className="snplay:sr-only">Mobile navigation</SheetTitle>
      <Branding
        className={cn(
          'snplay:sticky snplay:top-0 snplay:z-40',
          'snplay:bg-background snplay:p-6 snplay:shadow-sm',
        )}
      >
        <SheetClose asChild>
          <Button
            variant="ghost"
            className="snplay:absolute snplay:right-4 snplay:top-4"
            aria-label="Close Nav"
          >
            <XIcon className="snplay:size-5" />
            <span className="snplay:sr-only">Close Nav</span>
          </Button>
        </SheetClose>
        <SheetClose asChild>
          <Link href="/" className="snplay-can-focus snplay:inline-block snplay:transition">
            <img src={logoUrl} className="snplay:h-8" alt={brandName} />
          </Link>
        </SheetClose>
        <h1 className="snplay:mt-2 snplay:font-bold">
          Pack Everything. <span className="snplay:text-primary">Carry Nothing.</span>
        </h1>
      </Branding>
      <nav className="snplay:space-y-8 snplay:p-8">
        {siteLinkGroups.map((group) => (
          <LinkGroup as="section" key={group.id}>
            <span className="snplay-group-name">
              {group.id === 'brand' ? brandName : group.title}
            </span>
            <ul className="snplay:-mx-6 snplay:space-y-0.5">
              {group.links.map((link) => {
                const LinkComponent = link.external ? 'a' : Link
                return (
                  <li key={link.label}>
                    <SheetClose asChild>
                      <LinkComponent
                        href={link.url}
                        target={link.target}
                        className="snplay-menu-link"
                        onClick={(e) => {
                          if ('action' in link && link.action) {
                            e.preventDefault()
                            handleAccountLinkAction(link.action)
                          }
                        }}
                      >
                        {link.label}
                      </LinkComponent>
                    </SheetClose>
                  </li>
                )
              })}
            </ul>
          </LinkGroup>
        ))}
      </nav>
      <footer
        className={cn(
          'snplay:sticky snplay:inset-x-0 snplay:bottom-0',
          'snplay:z-40 snplay:bg-background snplay:p-4',
          'snplay:shadow-[0_-1px_0_0_rgb(0_0_0/0.1)]',
        )}
      >
        <SheetClose asChild>
          <Button variant="brand" href="/book/ship" size="xl">
            Ship Now
            <MoveRightIcon className="snplay:size-6" />
          </Button>
        </SheetClose>
      </footer>
    </SheetContent>
  )
}
